<script setup lang="ts">
import { useField, useFormValues } from 'vee-validate'
import { inject } from 'vue'
import { onMounted, ref, watch } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import { KontaktnaKarta } from '/@src/client-portal'
import * as apisPortal from '/@src/client-portal/api'
import { useUserSession } from '/@src/stores/userSession'

const { t } = useI18n()
const userSession = useUserSession()
const getApi = new apisPortal.SubjektyApi()
const isReadOnly = inject<boolean>('isReadOnly')
const readOnlyResetKey = inject<string | number | undefined>('readOnlyResetKey')
const korespondencneAdresy = ref<KontaktnaKarta['adresa'][]>()
const formValues = useFormValues()
const vybranaAdresa = ref<KontaktnaKarta['adresa']>()
const { setValue } = useField('ziadatel.korespondencnaAdresa')
const isChecked = ref(false)

onMounted(async () => {
  if (!isReadOnly) {
    korespondencneAdresy.value = await getApi
      .portalGetSubjektKontaktListSubjekty(userSession?.user?.subjekt?.subjektId!)
      .then((res) => {
        const kontakty = res.data as ReadonlyArray<KontaktnaKarta>
        if (!kontakty) {
          return []
        }

        // ISPO-3242 - zobrazime len tie kontakty, ktore maju adresu
        return kontakty
          .filter((kontakt) => kontakt.adresa?.adresaUplna)
          .map(({ adresa }) => adresa)
      })
  }
})

watch(
  () => formValues.value,
  () => {
    if (formValues.value?.ziadatel?.korespondencnaAdresa) {
      isChecked.value = true
    }
    vybranaAdresa.value = formValues.value?.ziadatel?.korespondencnaAdresa
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <template v-if="!isReadOnly">
    <IDSKCheckbox
      id="ziadatel-korespondencna-adresa-checkbox-fo"
      :checked="isChecked ? true : false"
      :is-in-checkboxes="true"
      @is-checked="
        (value) => {
          if (value === false) {
            setValue(null)
            vybranaAdresa = null
          }

          isChecked = value
        }
      "
    >
      <template #default>
        <IDSKParagraph>
          {{ t('zoppm.step7.fyzickaOsoba.checkboxHeading') }}</IDSKParagraph
        >
      </template>
      <template #hidden-content>
        <IDSKHeading as="h3" variant="m">
          {{ t('zoppm.step7.fyzickaOsoba.checkboxTitle') }}
        </IDSKHeading>
        <IDSKParagraph>
          {{ t('zoppm.step7.fyzickaOsoba.checkboxDescription.part1') }}
          <IDSKLink :href="'/app/subjekt/kontakty'" role="button">
            {{ t('zoppm.step7.fyzickaOsoba.checkboxDescription.part2') }}
          </IDSKLink>
        </IDSKParagraph>
        <ValidationWrapper
          v-if="korespondencneAdresy?.length > 0 && !vybranaAdresa"
          v-slot="{ modelValue, ...slotProps }"
          name="ziadatel.korespondencnaAdresa"
        >
          <IDSKAutocomplete
            v-bind="slotProps"
            id="ziadatel-korespondencna-adresa-fo"
            :key="modelValue"
            :label="t('zoppm.step7.fyzickaOsoba.korespondencnaAdresaHint')"
            placeholder=""
            :options="korespondencneAdresy"
            option-object-value="adresaUplna"
            option-object-label="adresaUplna"
            :model-value="modelValue"
            @update:model-value="(adresa: any) => {
            vybranaAdresa =  adresa
            return adresa
          }"
          />
        </ValidationWrapper>

        <IDSKParagraph v-if="vybranaAdresa">
          <Address :data="vybranaAdresa" />
        </IDSKParagraph>

        <IDSKLink
          v-if="vybranaAdresa"
          class="mb-6"
          href="#"
          no-visited-state
          @click.prevent="
            () => {
              setValue(null)
              vybranaAdresa = null
            }
          "
        >
          {{ t('zoppm.step7.fyzickaOsoba.odobratKorespondencnuAdresu') }}
        </IDSKLink>
      </template>
    </IDSKCheckbox>
  </template>

  <template v-else>
    <IDSKCheckbox :key="Math.random()" id="ziadatel-korespondencna-adresa-checkbox-fo">
      <template #default>
        <IDSKParagraph>
          {{ t('zoppm.step7.fyzickaOsoba.checkboxHeading') }}</IDSKParagraph
        >
      </template>
      <template #hidden-content>
        <IDSKHeading as="h3" variant="m">
          {{ t('zoppm.step7.fyzickaOsoba.checkboxTitle') }}
        </IDSKHeading>
        <IDSKParagraph>
          {{ t('zoppm.step7.fyzickaOsoba.checkboxDescription.part1') }}
          {{ t('zoppm.step7.fyzickaOsoba.checkboxDescription.part2') }}
        </IDSKParagraph>
        <IDSKAutocomplete
          id="ziadatel-korespondencna-adresa-fo-read"
          :key="readOnlyResetKey"
          :label="t('zoppm.step7.fyzickaOsoba.korespondencnaAdresaHint')"
          placeholder=""
          :options="[]"
          option-object-value="adresaUplna"
          option-object-label="adresaUplna"
        />
      </template>
    </IDSKCheckbox>
  </template>
</template>

<style lang="scss"></style>
